/**
 * Réduction d'état de la run — **le seul endroit qui décide des valeurs affichées** (M2-07).
 *
 * Principe (DECISIONS §8, §1.3 A-1) : le serveur est l'autorité, et **aucune projection locale
 * n'existe plus**. `serveur` porte l'état reçu de `POST /runs` ou d'un `GET /runs/:id` : c'est la
 * seule source des éclats, des jokers et des PV affichés. Le store ne sait pas calculer un prix,
 * un gain ni un déblocage.
 *
 * Deux exceptions, toutes deux des **faits** et non des gains :
 * 1. `pvObserves` : les PV constatés par la simulation pendant une vague (le serveur les recoupe
 *    à la clôture, DECISIONS §8.3c). Ils ne peuvent que descendre, et le serveur reprend la main
 *    dès qu'il répond ;
 * 2. `toursPosees` : les instances de tours créées par le serveur, miroir de `GET /runs/:id`.
 *
 * Le reste (`optionsCartes`, `finRun`, `refus`) ne contient que des réponses serveur.
 */
import type { ErreurApi } from '../api/erreurs';
import type { ResultatVague, TourServeur } from '../ui/game-canvas/types';
import type { OptionCarte, RunEndResponse, RunState } from '@elemora/shared/api';

/** Où en est la run côté écran. */
export type PhaseRun =
  | 'inactive'
  | 'chargement'
  | 'prete'
  | 'vague'
  | 'cloture'
  | 'choix-carte'
  | 'fin-niveau'
  | 'defaite'
  /**
   * Arrêt volontaire (mode illimité, §7 pt 7 / A-25). Distinct de `defaite` : le parcours n'est
   * pas submergé, c'est le joueur qui a arrêté la run. La phase porte le **motif**, que l'écran de
   * fin affiche tel quel.
   */
  | 'abandon'
  | 'perdue'
  | 'erreur';

/** Raison d'une run déclarée perdue côté client. */
export type RaisonRunPerdue = 'rechargement' | 'contenu-change';

/** Issue d'une vague close, telle que le serveur l'a validée. */
export interface FinDeVague {
  readonly faits: ResultatVague;
  readonly eclatsGagnes: number;
  readonly eclatsRun: number;
  /** Index de la vague suivante, `null` si c'était la dernière. */
  readonly vagueSuivante: number | null;
}

/** Fin de run validée par le serveur (`POST /runs/:id/end`). */
export interface FinDeRun {
  /** `victoire` (niveau validé), `defaite` (PV à 0) ou `abandon` (arrêt volontaire, §7.6). */
  readonly raison: 'victoire' | 'defaite' | 'abandon';
  readonly reponse: RunEndResponse;
}

export interface EtatRun {
  readonly phase: PhaseRun;
  readonly runId: string | null;
  readonly levelId: string;
  /** Version de contenu à laquelle la run est rattachée (renvoyée par l'API). */
  readonly contentVersion: string | null;
  /** État d'autorité tel que reçu du serveur — **seule source** des valeurs affichées. */
  readonly serveur: RunState | null;
  /** Tours posées, telles que le serveur les a créées (identifiants d'instance inclus). */
  readonly toursPosees: readonly TourServeur[];
  /** Tour de contenu choisie dans la barre basse (à poser). */
  readonly tourSelectionnee: string | null;
  /** Tour déjà posée sélectionnée pour être améliorée. */
  readonly tourAmelioration: TourServeur | null;
  /** Index (0-based) de la prochaine vague à lancer. */
  readonly vagueIndex: number;
  /** PV constatés par la simulation pendant la vague courante (fait, jamais un gain). */
  readonly pvObserves: number | null;
  readonly derniereVague: FinDeVague | null;
  /** Options de cartes tirées par le serveur après une vague close. */
  readonly optionsCartes: readonly OptionCarte[] | null;
  /** Carte bonus de boss accordée à la vague (déjà débloquée), `null` s'il n'y en a pas. */
  readonly bonusCardId: string | null;
  readonly finRun: FinDeRun | null;
  readonly refus: ErreurApi | null;
  readonly perdue: RaisonRunPerdue | null;
  /** Une action serveur est en cours (verrou d'interface, jamais une valeur de jeu). */
  readonly actionEnCours: boolean;
}

export const ETAT_RUN_INITIAL: EtatRun = {
  phase: 'inactive',
  runId: null,
  levelId: '',
  contentVersion: null,
  serveur: null,
  toursPosees: [],
  tourSelectionnee: null,
  tourAmelioration: null,
  vagueIndex: 0,
  pvObserves: null,
  derniereVague: null,
  optionsCartes: null,
  bonusCardId: null,
  finRun: null,
  refus: null,
  perdue: null,
  actionEnCours: false,
};

export type ActionRun =
  | { readonly type: 'chargement'; readonly levelId: string }
  | {
      readonly type: 'run-prete';
      readonly runId: string;
      readonly run: RunState;
      readonly tours: readonly TourServeur[];
      readonly vagueIndex: number;
    }
  | {
      readonly type: 'etat-serveur';
      readonly run: RunState;
      readonly tours?: readonly TourServeur[];
      readonly vagueIndex?: number;
    }
  | { readonly type: 'erreur'; readonly erreur: ErreurApi }
  | { readonly type: 'action-debut' }
  | { readonly type: 'action-fin' }
  | { readonly type: 'selection-tour'; readonly tourId: string | null }
  | { readonly type: 'selection-tour-posee'; readonly tour: TourServeur | null }
  | { readonly type: 'refus'; readonly erreur: ErreurApi }
  | { readonly type: 'effacer-refus' }
  | { readonly type: 'pose-confirmee'; readonly tour: TourServeur; readonly run: RunState }
  | {
      readonly type: 'amelioration-confirmee';
      readonly tour: TourServeur;
      readonly run: RunState;
    }
  | { readonly type: 'vague-demarree'; readonly index: number }
  | { readonly type: 'vague-close'; readonly faits: ResultatVague }
  | {
      readonly type: 'vague-validee';
      readonly eclatsGagnes: number;
      readonly eclatsRun: number;
      readonly vagueSuivante: number | null;
      readonly run: RunState;
    }
  | {
      readonly type: 'options-cartes';
      readonly options: readonly OptionCarte[];
      readonly bonusCardId: string | null;
    }
  | { readonly type: 'carte-choisie'; readonly run: RunState }
  | {
      readonly type: 'fin-run';
      readonly raison: 'victoire' | 'defaite' | 'abandon';
      readonly reponse: RunEndResponse;
    }
  | { readonly type: 'pv-observes'; readonly pv: number }
  | { readonly type: 'run-perdue'; readonly raison: RaisonRunPerdue }
  | { readonly type: 'reinitialiser' };

/** Valeurs affichables. Les éclats et les jokers viennent **uniquement** du serveur. */
export interface ValeursAffichees {
  readonly eclats: number;
  readonly jokers: number;
  readonly pv: number;
  /** `serveur` : valeur d'autorité ; `observation` : PV constatés pendant la vague. */
  readonly sourcePv: 'serveur' | 'observation';
}

/**
 * Applique l'état d'autorité. Aucun autre calcul n'existe : pas de dépense projetée, pas de gain
 * estimé, pas de joker déduit localement.
 */
export function valeursAffichees(etat: EtatRun): ValeursAffichees {
  const serveur = etat.serveur;
  if (serveur === null) {
    return { eclats: 0, jokers: 0, pv: 0, sourcePv: 'serveur' };
  }
  const pvObserves = etat.pvObserves;
  const pv =
    pvObserves === null ? serveur.pvParcours : Math.min(serveur.pvParcours, Math.max(0, pvObserves));
  return {
    eclats: serveur.eclatsRun,
    jokers: serveur.jokers,
    pv,
    sourcePv: pvObserves === null ? 'serveur' : 'observation',
  };
}

/** Numéro de vague affiché (1-based). */
export function numeroVague(etat: EtatRun): number {
  return etat.vagueIndex + 1;
}

/**
 * Prochaine vague à lancer, déduite des statuts renvoyés par `GET /runs/:id`.
 *
 * Une vague `close` ne se rejoue pas : la première non close est la suivante. Toutes closes
 * (niveau terminé), on renvoie la dernière pour ne pas afficher « vague 6 / 5 ».
 */
export function prochaineVague(vagues: readonly { index: number; status: string }[]): number {
  const suivante = vagues.find((vague) => vague.status !== 'close');
  if (suivante !== undefined) return suivante.index;
  return Math.max(0, vagues.length - 1);
}

/**
 * Fin d'une vague : le serveur a recalculé les gains. `vagueSuivante` décide de la phase —
 * choix de carte, puis vague suivante, ou fin de niveau.
 */
function apresVagueValidee(etat: EtatRun, action: Extract<ActionRun, { type: 'vague-validee' }>): EtatRun {
  const derniere = etat.derniereVague;
  return {
    ...etat,
    serveur: action.run,
    pvObserves: null,
    tourSelectionnee: null,
    tourAmelioration: null,
    vagueIndex: action.vagueSuivante ?? etat.vagueIndex,
    phase: action.vagueSuivante === null ? 'cloture' : 'choix-carte',
    derniereVague:
      derniere === null
        ? null
        : {
            ...derniere,
            eclatsGagnes: action.eclatsGagnes,
            eclatsRun: action.eclatsRun,
            vagueSuivante: action.vagueSuivante,
          },
  };
}

/** Réduction pure. Toute transition passe par ici : aucun composant ne modifie un champ à la main. */
export function reduire(etat: EtatRun, action: ActionRun): EtatRun {
  switch (action.type) {
    case 'chargement':
      return { ...ETAT_RUN_INITIAL, phase: 'chargement', levelId: action.levelId };

    case 'run-prete':
      return {
        ...ETAT_RUN_INITIAL,
        phase: 'prete',
        runId: action.runId,
        levelId: action.run.levelId,
        contentVersion: action.run.contentVersion,
        serveur: action.run,
        toursPosees: [...action.tours],
        vagueIndex: action.vagueIndex,
      };

    case 'etat-serveur':
      return {
        ...etat,
        serveur: action.run,
        ...(action.tours === undefined ? {} : { toursPosees: [...action.tours] }),
        ...(action.vagueIndex === undefined ? {} : { vagueIndex: action.vagueIndex }),
      };

    case 'erreur':
      return { ...etat, phase: 'erreur' };

    case 'action-debut':
      return { ...etat, actionEnCours: true, refus: null };

    case 'action-fin':
      return { ...etat, actionEnCours: false };

    case 'selection-tour':
      // Choisir une tour à poser désélectionne la tour à améliorer : une seule intention à la fois.
      return { ...etat, tourSelectionnee: action.tourId, tourAmelioration: null };

    case 'selection-tour-posee':
      return { ...etat, tourAmelioration: action.tour, tourSelectionnee: null };

    case 'refus':
      return { ...etat, refus: action.erreur, actionEnCours: false };

    case 'effacer-refus':
      return { ...etat, refus: null };

    case 'pose-confirmee':
      return {
        ...etat,
        serveur: action.run,
        // Le serveur est la seule source : les tours affichées viennent de sa réponse.
        toursPosees: etat.toursPosees.some((tour) => tour.towerId === action.tour.towerId)
          ? etat.toursPosees
          : [...etat.toursPosees, action.tour],
        tourSelectionnee: null,
        actionEnCours: false,
        refus: null,
      };

    case 'amelioration-confirmee':
      return {
        ...etat,
        serveur: action.run,
        toursPosees: etat.toursPosees.map((tour) =>
          tour.towerId === action.tour.towerId ? action.tour : tour,
        ),
        tourAmelioration: action.tour,
        actionEnCours: false,
        refus: null,
      };

    case 'vague-demarree':
      if (etat.phase !== 'prete' && etat.phase !== 'choix-carte') return etat;
      return {
        ...etat,
        phase: 'vague',
        vagueIndex: action.index,
        pvObserves: null,
        derniereVague: null,
        optionsCartes: null,
        bonusCardId: null,
        actionEnCours: false,
        refus: null,
      };

    case 'pv-observes': {
      if (etat.serveur === null) return etat;
      // Les PV ne remontent jamais : une observation tardive ne peut pas « soigner » le parcours.
      const actuel = etat.pvObserves ?? etat.serveur.pvParcours;
      const borne = Math.max(0, Math.min(etat.serveur.pvParcours, action.pv));
      if (borne >= actuel) return etat;
      return { ...etat, pvObserves: borne };
    }

    case 'vague-close':
      return {
        ...etat,
        phase: 'cloture',
        actionEnCours: true,
        derniereVague: {
          faits: action.faits,
          eclatsGagnes: 0,
          eclatsRun: etat.serveur?.eclatsRun ?? 0,
          vagueSuivante: null,
        },
      };

    case 'vague-validee':
      return apresVagueValidee(etat, action);

    case 'options-cartes':
      return {
        ...etat,
        phase: 'choix-carte',
        optionsCartes: [...action.options],
        bonusCardId: action.bonusCardId,
        actionEnCours: false,
      };

    case 'carte-choisie':
      return {
        ...etat,
        phase: 'prete',
        serveur: action.run,
        optionsCartes: null,
        bonusCardId: null,
        actionEnCours: false,
        refus: null,
      };

    case 'fin-run':
      return {
        ...etat,
        phase: action.raison === 'victoire' ? 'fin-niveau' : action.raison,
        finRun: { raison: action.raison, reponse: action.reponse },
        actionEnCours: false,
      };

    case 'run-perdue':
      return { ...etat, phase: 'perdue', perdue: action.raison };

    case 'reinitialiser':
      return ETAT_RUN_INITIAL;

    default:
      return etat;
  }
}
